import { OnboardingButtonType } from "@components/atoms/buttons/onboardingButton/onboardingButton";
import { LottieOptions } from "lottie-react";
import { CSSProperties } from "react";

export enum ScreenType {
    SingleOption = "SingleOption",
    MultiplyOption = "MultiplyOption",
    ImageScreen = "ImageScreen",
    SkipScreen = "SkipScreen",
    DependentScreen = "DependentScreen",
    LoaderScreen = "LoaderScreen",
    InfoScreen = "InfoScreen",
    InputScreen = "InputScreen",
    EmailScreen = "EmailScreen",
    YesNoScreen = "YesNoScreen",
    ChartScreen = "ChartScreen",
    ProductOnboardingScreen = "ProductOnboardingScreen",
    PaywallScreen = "PaywallScreen",
    IndianFirstScreen = "IndianFirstScreen",
    LevelIndicatorScreen = "LevelIndicatorScreen",
    LottieScreen = "LottieScreen",
    SliderScreen = "SliderScreen",
    LoaderWithPopupsScreen = "LoaderWithPopupsScreen",
    IntroLessonScreen = "IntroLessonScreen",
    IntroLessonFinishedScreen = "IntroLessonFinishedScreen",
    FunnelFoxInfoScreen = "FunnelFoxInfoScreen",
    FunnelFoxFirstScreen = "FunnelFoxFirstScreen",
    FunnelFoxSingleOption = "FunnelFoxSingleOption",
    FunnelFoxMultiplyOption = "FunnelFoxMultiplyOption",
    FunnelFoxLevelIndicatorScreen = "FunnelFoxLevelIndicatorScreen",
    FunnelFoxLoaderWithPopupsScreen = "FunnelFoxLoaderWithPopupsScreen",
    FunnelFoxEmailScreen = "FunnelFoxEmailScreen",
    FunnelFoxInputScreen = "FunnelFoxInputScreen",
    FunnelFoxYesNoScreen = "FunnelFoxYesNoScreen",
    FunnelFoxChartScreen = "FunnelFoxChartScreen",
    FunnelFoxSliderScreen = "FunnelFoxSliderScreen",
    FunnelFoxDependentScreen = "FunnelFoxDependentScreen",
    AppleEmailScreen = "AppleEmailScreen",
    AppleChartScreen = "AppleChartScreen",
    AppleFirstScreen = "AppleFirstScreen"
}

export type DescriptionPart = string | { text: string; class: string };
export type TitlePart = string | { text: string; class: string };

export interface IOption {
    id: string;
    value: string;
    icon?: string;
    emoji?: string;
    answer?: {
        title: string | TitlePart[];
        description?: string;
        textMark?: string;
    };
}

interface BaseScreenData {
    id: string;
    title: string;
    description?: string;
    data: IOption[];
}

interface SingleOptionScreen extends BaseScreenData {
    screenType: ScreenType.SingleOption;
    buttonType: OnboardingButtonType;
    dependentStep?: number;
    preTitle?: string | TitlePart[];
}

interface FunnelFoxSingleOptionScreen extends BaseScreenData {
    screenType: ScreenType.FunnelFoxSingleOption;
    buttonType: OnboardingButtonType;
    dependentStep?: number;
    preTitle?: string | TitlePart[];
    preTitleClassName?: string;
    titleClassName?: string;
    descriptionClassName?: string;
}

interface MultiplyOptionScreen extends BaseScreenData {
    screenType: ScreenType.MultiplyOption;
    nextButtonTitle?: string;
}

interface FunnelFoxMultiplyOptionScreen extends BaseScreenData {
    screenType: ScreenType.FunnelFoxMultiplyOption;
    isLinksVisible?: boolean;
    buttonClassName?: string;
    nextButtonTitle?: string;
}

interface ImageScreen extends BaseScreenData {
    screenType: ScreenType.ImageScreen;
}

interface DependentScreen extends Omit<BaseScreenData, "title" | "description" | "data"> {
    image?: string;
    screenType: ScreenType.DependentScreen;
    dependentStep: number;
}

interface FunnelFoxDependentScreen extends Omit<BaseScreenData, "title" | "description" | "data"> {
    image?: string;
    screenType: ScreenType.FunnelFoxDependentScreen;
    dependentStep: number;
    buttonClassName?: string;
    nextButtonTitle?: string;
}

interface LoaderScreen extends Omit<BaseScreenData, "data" | "description"> {
    screenType: ScreenType.LoaderScreen;
    feedbacks: Array<{
        author: string;
        title?: string;
        comment: string;
    }>;
    loadingText: string;
    loadingTime: number; // seconds
}

interface InfoScreen extends Omit<BaseScreenData, "data" | "title" | "description"> {
    title: string | TitlePart[];
    image?: string;
    screenType: ScreenType.InfoScreen;
    imgStyles?: CSSProperties;
    descriptionClassName?: string;
    nextButtonTitle?: string;
    description?: string | DescriptionPart[];
    textMark?: {
        title?: string;
        description?: string;
    };
    titleClassName?: string;
    buttonPulse?: boolean;
    buttonClassName?: string;
    imgClassName?: string;
}

interface FunnelFoxInfoScreen extends Omit<BaseScreenData, "data" | "title" | "description"> {
    title: string | TitlePart[];
    image?: string;
    screenType: ScreenType.FunnelFoxInfoScreen;
    imgStyles?: CSSProperties;
    descriptionClassName?: string;
    nextButtonTitle?: string;
    description?: string | DescriptionPart[];
    textMark?: {
        title?: string;
        description?: string;
    };
    titleClassName?: string;
    buttonPulse?: boolean;
    buttonClassName?: string;
    imgClassName?: string;
    isLinksVisible?: boolean;
}

interface FunnelFoxFirstScreen extends Omit<BaseScreenData, "data" | "title" | "description"> {
    title: string | TitlePart[];
    image?: string;
    screenType: ScreenType.FunnelFoxFirstScreen;
    imgStyles?: CSSProperties;
    descriptionClassName?: string;
    nextButtonTitle?: string;
    description?: string | DescriptionPart[];
    textMark?: {
        title?: string;
        description?: string;
    };
    titleClassName?: string;
    buttonPulse?: boolean;
    buttonClassName?: string;
    imgClassName?: string;
    isLinksVisible?: boolean;
    isButtonVisible?: boolean;
    isProgressBarVisible?: boolean;
}

interface InputScreen extends Omit<BaseScreenData, "data"> {
    screenType: ScreenType.InputScreen;
    inputId: string;
    placeholder?: string;
}

interface FunnelFoxInputScreen extends Omit<BaseScreenData, "data"> {
    screenType: ScreenType.FunnelFoxInputScreen;
    inputId: string;
    placeholder?: string;
    nextButtonTitle?: string;
}

interface EmailScreen extends Omit<BaseScreenData, "data" | "title"> {
    screenType: ScreenType.EmailScreen;
    placeholder?: string;
    title: string | TitlePart[];
    animationData?: LottieOptions["animationData"];
}

interface AppleEmailScreen extends Omit<BaseScreenData, "data" | "title"> {
    screenType: ScreenType.AppleEmailScreen;
    placeholder?: string;
    title: string;
}

interface AppleFirstScreen {
    screenType: ScreenType.AppleFirstScreen;
    id: string;
}

interface AppleChartScreen {
    screenType: ScreenType.AppleChartScreen;
    id: string;
    title: string;
    description?: string;
}

interface FunnelFoxEmailScreen extends Omit<BaseScreenData, "data" | "title"> {
    screenType: ScreenType.FunnelFoxEmailScreen;
    placeholder?: string;
    title: string | TitlePart[];
    animationData?: LottieOptions["animationData"];
    description?: string;
}

interface YesNoScreen extends Omit<BaseScreenData, "title"> {
    image?: string;
    screenType: ScreenType.YesNoScreen;
    title: string | TitlePart[];
}

interface FunnelFoxYesNoScreen extends Omit<BaseScreenData, "title"> {
    image?: string;
    screenType: ScreenType.FunnelFoxYesNoScreen;
    title: string | TitlePart[];
}

interface ChartScreen extends Omit<BaseScreenData, "data" | "title" | "textButton"> {
    screenType: ScreenType.ChartScreen;
    dependentStep?: number;
    title: string | TitlePart[];
    textButton?: string;
}

interface FunnelFoxChartScreen extends Omit<BaseScreenData, "data" | "title" | "textButton"> {
    screenType: ScreenType.FunnelFoxChartScreen;
    dependentStep?: number;
    title: string | TitlePart[];
    textButton?: string;
}
interface ProductOnboardingScreen {
    id: string;
    screenType: ScreenType.ProductOnboardingScreen;
    images: string[];
    title: string;
    description: string;
}

interface PaywallScreen {
    screenType: ScreenType.PaywallScreen;
    id: string;
}

interface IndianFirstScreen extends BaseScreenData {
    image?: string;
    screenType: ScreenType.IndianFirstScreen;
}

interface LevelIndicatorScreen extends Omit<BaseScreenData, "data" | "title"> {
    screenType: ScreenType.LevelIndicatorScreen;
    title: string | TitlePart[];
    image?: string;
    textMark?: {
        title?: string;
        description?: string;
        icon?: string;
    };
}

interface FunnelFoxLevelIndicatorScreen extends Omit<BaseScreenData, "data" | "title"> {
    screenType: ScreenType.FunnelFoxLevelIndicatorScreen;
    title: string | TitlePart[];
    image?: string;
    textMark?: {
        title?: string;
        description?: string;
        icon?: string;
    };
    description?: string;
    buttonClassName?: string;
    nextButtonTitle?: string;
    steps?: {
        focusStep?: number;
        motivationStep?: number;
        timeStep?: number;
        skillsStep?: number;
    };
}

interface LottieScreen {
    screenType: ScreenType.LottieScreen;
    id: string;
    animationData: LottieOptions["animationData"];
    title: string | TitlePart[];
    description?: string;
}

interface SliderScreen {
    screenType: ScreenType.SliderScreen;
    id: string;
    textButton: string;
    slides: React.ReactNode[];
}

interface FunnelFoxSliderScreen {
    screenType: ScreenType.FunnelFoxSliderScreen;
    id: string;
    textButton: string;
    slides: React.ReactNode[];
}

interface LoaderWithPopupsScreen extends Omit<BaseScreenData, "data" | "description"> {
    screenType: ScreenType.LoaderWithPopupsScreen;
    feedbacks: Array<{
        author: string;
        title?: string;
        comment: string;
    }>;
}

interface FunnelFoxLoaderWithPopupsScreen extends Omit<BaseScreenData, "data" | "description"> {
    screenType: ScreenType.FunnelFoxLoaderWithPopupsScreen;
    feedbacks: Array<{
        author: string;
        title?: string;
        comment: string;
    }>;
}

interface IntroLessonScreen {
    screenType: ScreenType.IntroLessonScreen;
    id: string;
    title: string;
    description: string;
    image: string;
    courseId: number;
    topicId: number;
    lessonId: number;
}

interface IntroLessonFinishedScreen {
    screenType: ScreenType.IntroLessonFinishedScreen;
    id: string;
    title: string;
    description: string;
}

export type IScreenData =
    | SingleOptionScreen
    | FunnelFoxSingleOptionScreen
    | MultiplyOptionScreen
    | FunnelFoxMultiplyOptionScreen
    | ImageScreen
    | DependentScreen
    | FunnelFoxDependentScreen
    | InfoScreen
    | FunnelFoxInfoScreen
    | FunnelFoxFirstScreen
    | LoaderScreen
    | InputScreen
    | FunnelFoxInputScreen
    | EmailScreen
    | FunnelFoxEmailScreen
    | YesNoScreen
    | FunnelFoxYesNoScreen
    | ChartScreen
    | FunnelFoxChartScreen
    | ProductOnboardingScreen
    | PaywallScreen
    | IndianFirstScreen
    | LevelIndicatorScreen
    | FunnelFoxLevelIndicatorScreen
    | LottieScreen
    | SliderScreen
    | FunnelFoxSliderScreen
    | LoaderWithPopupsScreen
    | FunnelFoxLoaderWithPopupsScreen
    | IntroLessonScreen
    | IntroLessonFinishedScreen
    | AppleEmailScreen
    | AppleFirstScreen;

export interface IAnswersData {
    step: number;
    data: IOption | IOption[];
    question: string;
}
